import { useForm } from 'react-hook-form';
import { toast, type ToastContentProps } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { TextareaField } from '@/components/form/fields/textarea-field';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  type DialogProps,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertType } from '@/entities/alert-type';
import { ResetScore } from '@/task-manager/api/mutations/reset-score';
import { GetTask } from '@/task-manager/api/queries/get-task';
import {
  ResetReason,
  ResetReasonSchema,
} from '@/task-manager/entities/reset-reason';
import { TaskCardType } from '@/task-manager/entities/task-card-type';
import type { TaskDetails } from '@/task-manager/entities/task-details';
import type { TaskPreview } from '@/task-manager/entities/task-preview';
import { TaskStatus } from '@/task-manager/entities/task-status';

export type RequestResetDialogProps = {
  cancelText?: string;
  task: TaskPreview | TaskDetails;
} & Pick<DialogProps, 'open' | 'onClose'>;

function shouldShowResetDialog(task: TaskPreview | TaskDetails): boolean {
  if (task.cardType !== TaskCardType.ALERT) return false;

  const alertType = 'alert' in task ? task.alert?.type : undefined;
  if (!alertType) return false;

  const allowedTypes = [
    AlertType.SAFETY,
    AlertType.PERFORMANCE,
    AlertType.IMBALANCE,
    AlertType.SOC_OFFSET,
  ];

  return allowedTypes.includes(alertType);
}

export function RequestResetDialog({
  cancelText,
  task,
  open,
  onClose,
}: RequestResetDialogProps) {
  const form = useForm<FormInputs>({
    resolver: zodResolver(formSchema),
  });
  const requestReset = ResetScore.useMutation();
  const taskDetails = GetTask.useQuery(task);
  const { isValid } = form.formState;

  let device = undefined;
  if ('device' in task) {
    device = task.device;
  } else if (taskDetails.data) {
    device = taskDetails.data.task.device;
  }

  async function handleSubmit(data: FormInputs) {
    await toast.promise(
      requestReset.mutateAsync({
        taskId: task.id,
        ...data,
      }),
      {
        pending: 'Sending request...',
        error: {
          render: function ({ data: { status } }: ToastContentProps<any>) {
            if (status === 409) {
              return 'A request to reset the safety score has already been made for this task';
            }
            return 'An unexpected error happened. Please try again.';
          },
        },
        success: 'Request submitted successfully.',
      },
    );
    onClose(false);
  }

  if (task.status !== TaskStatus.DONE || !shouldShowResetDialog(task)) {
    return null;
  }

  return (
    <Dialog
      className="mt-24"
      open={open}
      onClose={onClose}
      data-testid="safety-score-reset-dialog"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogTitle>Request a safety score reset?</DialogTitle>
          <DialogDescription>
            Completing a task from an alert opens the option to request a safety
            score reset. The safety score will be set to zero, and any
            subsequent safety concerns will appear in the Alerts and Tasks
            dashboards.
          </DialogDescription>
          {device ? (
            <div className="mt-4 border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                <div className="grid grid-cols-4 gap-4 px-4 py-2">
                  <dt className="text-sm leading-6">Top ID</dt>
                  <dd className="col-span-3 mt-1 text-sm font-medium leading-6">
                    {device.topFrontendId}
                  </dd>
                </div>
                <div className="grid grid-cols-4 gap-4 px-4 py-2">
                  <dt className="text-sm leading-6">
                    {device.frontendLevel
                      ? capitalizeFirstLetter(device.frontendLevel)
                      : 'Unit ID'}
                  </dt>
                  <dd className="col-span-3 mt-1 text-sm font-medium leading-6">
                    {device.unitFrontendId}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}
          <DialogBody className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      data-testid="safety-score-reset-reason"
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select reason" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectGroup>
                          {Object.values(ResetReason).map((v) => (
                            <SelectItem key={v} value={v.toString()}>
                              {v.toString()}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <TextareaField
                      {...field}
                      data-testid="safety-score-reset-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DialogBody>
          <DialogActions>
            <Button
              variant="ghost"
              type="button"
              onClick={() => onClose(false)}
            >
              {cancelText ?? 'Cancel'}
            </Button>
            <Button
              variant="primaryOutline"
              type="submit"
              disabled={!isValid || requestReset.isLoading}
              data-testid="safety-score-reset-submit"
            >
              Submit request
            </Button>
          </DialogActions>
        </form>
      </Form>
    </Dialog>
  );
}

const formSchema = z.object({
  description: z.string().min(1),
  reason: ResetReasonSchema,
});

type FormInputs = z.infer<typeof formSchema>;

function capitalizeFirstLetter(v: string) {
  return v.charAt(0).toUpperCase() + v.slice(1);
}
